const DEFAULT_REDIRECT = '/';
// Anima-owned registrable domains a cross-origin redirect may target — this
// list is the open-redirect guard.
const ALLOWED_HOSTNAMES = ['animaapp.com', 'agentgrid.io'];
// URL.hostname keeps the brackets for IPv6, so '[::1]' is the correct literal.
const LOOPBACK_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]'];
const LOOPBACK_PROTOCOLS = ['http:', 'https:'];

const getWindowLocation = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.location;
};

const getCurrentOrigin = ({ currentOrigin, location } = {}) => {
  if (currentOrigin) {
    return currentOrigin;
  }
  return location?.origin || getWindowLocation()?.origin;
};

export const isAllowedRedirectHostname = (hostname) => {
  const normalizedHostname = hostname?.toLowerCase();
  if (!normalizedHostname) {
    return false;
  }
  return ALLOWED_HOSTNAMES.some(
    (allowed) => normalizedHostname === allowed || normalizedHostname.endsWith(`.${allowed}`)
  );
};

/**
 * Local development only: the services run on plain-http loopback ports
 * (AgentGrid 5176, public-api 3789), which no production allowlist can express.
 * Still protocol-restricted — a loopback hostname alone must not admit
 * javascript:, data: or ftp: targets.
 */
const isLoopbackDevRedirect = (url) =>
  process.env.NODE_ENV === 'development' &&
  LOOPBACK_PROTOCOLS.includes(url.protocol) &&
  LOOPBACK_HOSTNAMES.includes(url.hostname?.toLowerCase());

export const sanitizeRedirect = (redirect, options = {}) => {
  const { fallback = DEFAULT_REDIRECT } = options;

  if (typeof redirect !== 'string') {
    return fallback;
  }

  const value = redirect.trim();
  if (!value || value.includes('\\')) {
    return fallback;
  }

  if (value.startsWith('/')) {
    return value.startsWith('//') ? fallback : value;
  }

  let url;
  try {
    url = new URL(value);
  } catch (err) {
    return fallback;
  }

  const currentOrigin = getCurrentOrigin(options);
  if (currentOrigin && url.origin === currentOrigin) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  if (url.protocol === 'https:' && isAllowedRedirectHostname(url.hostname)) {
    return url.href;
  }

  if (isLoopbackDevRedirect(url)) {
    return url.href;
  }

  return fallback;
};

export const isExternalRedirect = (redirect) => {
  if (typeof redirect !== 'string') {
    return false;
  }
  if (redirect.startsWith('https://')) {
    return true;
  }
  // http is external only in development, where sanitizeRedirect admits
  // loopback targets. Re-checked here — callers may pass unsanitized values.
  return process.env.NODE_ENV === 'development' && redirect.startsWith('http://');
};

export const isAgentGridFigmaCallback = (redirect) => {
  try {
    const url = new URL(redirect);
    const hostname = url.hostname.toLowerCase();
    const isAgentGrid = hostname === 'agentgrid.io' || hostname.endsWith('.agentgrid.io');
    return (
      url.pathname === '/auth/figma/callback' &&
      ((url.protocol === 'https:' && isAgentGrid) || isLoopbackDevRedirect(url))
    );
  } catch {
    return false;
  }
};

export const relayAgentGridFigmaCallback = async ({
  redirect,
  query,
  validateState,
  location = getWindowLocation()
}) => {
  const { code, error, error_description: description, state } = query || {};
  if ((!code && !error) || !location) {
    throw new Error('Figma result not attached');
  }
  await validateState(state);
  const callback = new URL(redirect);
  if (code) {
    callback.searchParams.set('code', code);
  } else {
    callback.searchParams.set('error', error);
    if (description) callback.searchParams.set('error_description', description);
  }
  location.replace(callback.toString());
};

export const navigateToExternalRedirect = (redirect, location = getWindowLocation()) => {
  if (!location) {
    return;
  }

  location.href = redirect;
};
